<template>
  <div
    class="n-file-message"
    :class="{
      'n-file-message--left': tooltipPosition === 'right',
      'n-file-message--right': tooltipPosition === 'left',
    }"
  >
    <n-avatar
      size="s"
      :name="message.name"
      :imageUrl="message.avatarUrl"
      v-if="displayAvatar"
      :enableTooltip="enableAvatarTooltip"
      class="n-file-message__avatar"
      :class="{
        'n-file-message__avatar--left': tooltipPosition === 'right',
        'n-file-message__avatar--right': tooltipPosition === 'left',
      }"
    ></n-avatar>
    <n-tooltip :position="tooltipPosition">
      <template #default>
        <div class="n-file-message__message-container" :style="getMessageStyle()">
          <n-icon icon="file"></n-icon>
          <a :href="message.mediaSrc" download :style="{ color: getMessageStyle().color }"
            >{{ message.fileName }} <span class="n-file-message__file-size">({{ fileSize }})</span></a
          >
        </div>
      </template>
      <template #message>
        <span>
          {{ getMessageTimestamp(message.timestampUtc) }}
          <n-icon icon="alert" fill="#ff6348" v-if="shouldShowAlertIcon(message)" />
          <n-icon
            icon="tick"
            :fill="isRead(message) ? '#57d272' : '#bdbdbd'"
            v-if="shouldShowTickIcon(message)"
          ></n-icon>
        </span>
      </template>
    </n-tooltip>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import moment from 'moment';
import tinycolor2 from 'tinycolor2';
import NIcon from '../../brand/icon/icon.vue';
import NTooltip from '../../control/tooltip/tooltip.vue';
import NAvatar from '../avatar/avatar.vue';
import { Message, FileMessage } from './message';
import { MessageStatus } from './messageStatus';
import { PropType } from 'vue';

const props = defineProps({
  outboundMessageColour: {
    type: String,
    default: '#062b80',
  },
  message: {
    type: Object as PropType<FileMessage>,
    required: true,
  },
  displayAvatar: {
    type: Boolean,
    default: false,
  },
  enableAvatarTooltip: {
    type: Boolean,
    default: false,
  },
  tooltipPosition: {
    type: String as PropType<'right' | 'left'>,
    default: 'right',
  },
});

function getMessageTimestamp(timestamp: Date): string {
  return moment(timestamp).format('HH:mm');
}

function shouldShowAlertIcon(m: Message) {
  return m.status === MessageStatus.DeliveryFailed;
}

function isRead(m: Message) {
  return m.status === MessageStatus.Read;
}

function shouldShowTickIcon(m: Message) {
  switch (m.status) {
    case MessageStatus.DeliverySucceeded:
    case MessageStatus.Read:
    case MessageStatus.Received:
    case MessageStatus.Sent:
      return true;
    default:
      return false;
  }
}

const fileSize = computed(() => {
  const i = Math.floor(Math.log(props.message.fileSize) / Math.log(1024));
  return `${Math.round(props.message.fileSize / 1024 ** i)} ${['B', 'kB', 'MB', 'GB', 'TB'][i]}`;
});

function getMessageStyle() {
  let backgroundColour = '#f5f5f5';
  let textColour = '#212121';

  if (props.message.direction === 'outbound') {
    backgroundColour = props.outboundMessageColour;
    textColour = tinycolor2(backgroundColour).isDark() ? '#fff' : '#212121';
  }

  if (props.message.status === MessageStatus.DeliveryFailed) {
    backgroundColour = '#ffdada';
    textColour = '#212121';
  }

  return {
    background: backgroundColour,
    color: textColour,
  };
}
</script>

<style lang="scss" scoped>
.n-file-message {
  max-width: 75%;
  width: max-content;
  word-wrap: break-word;
  display: flex;
  align-items: center;
  &--right {
    flex-direction: row-reverse;
    padding-right: 0.5em;
  }

  &--left {
    flex-direction: row;
    padding-left: 0.5em;
  }
  &__message-container {
    display: flex;
    align-items: center;
    max-width: 100%;
    max-height: 100%;
    border-radius: 0.2em;
    padding: 0.75em 1em;
    a {
      margin-left: 0.5rem;
    }
  }

  &__file-size {
    font-size: 0.8rem;
    text-decoration: none;
  }

  &__avatar {
    flex: 0 0 auto;
    &--left {
      margin-right: 0.5rem;
    }
    &--right {
      margin-left: 0.5rem;
    }
  }
}
</style>
